import { useEffect, useState } from "react";
import { fetchKudosPoints } from "../lib/api";
import { Coins, TrendingUp, Sparkles, Gift, Target } from "lucide-react";
import { Button } from "./ui/button";

/**
 * KudosPointsCard — Employee points balance + earned/spent + last kudos + target contribution.
 * Prominent CTA to open Rewards Catalog.
 */
export default function KudosPointsCard({ employeeId, onOpenCatalog }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchKudosPoints(employeeId).then(setData).catch(() => setData(null));
  }, [employeeId]);

  if (!data) return null;

  return (
    <div className="card-surface p-6 bg-gradient-to-br from-amber-50 via-white to-emerald-50" data-testid="kudos-points-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5 mb-1"><Coins className="w-3.5 h-3.5 text-amber-600" /> Takdir Puanlarım</div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-display font-bold text-amber-700" data-testid="kudos-balance">{data.balance}</span>
            <span className="text-xs text-slate-500">bakiye · {data.earned_total} kazandın · {data.spent_total} harcadın</span>
          </div>
        </div>
        <Button size="sm" onClick={onOpenCatalog} className="bg-amber-500 hover:bg-amber-600" data-testid="kudos-open-catalog">
          <Gift className="w-3.5 h-3.5 mr-1" /> Ödüle Çevir
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatMini icon={Sparkles} label="Toplam Takdir" value={data.recognitions_count} color="amber" />
        <StatMini icon={Target} label="Hedefime Katkı" value={data.target_contribution_points} suffix="pt" color="emerald" />
      </div>

      {data.history?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Son takdirler</div>
          <div className="space-y-1.5">
            {data.history.slice(0, 3).map((k) => (
              <div key={k.id} className="flex items-center justify-between text-sm text-slate-700 bg-white/60 rounded-lg px-3 py-1.5 border border-slate-100" data-testid={`kudos-hist-${k.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg">{(k.kudos_label || "🎉").split(" ")[0]}</span>
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{k.from_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{k.message}</div>
                  </div>
                </div>
                <span className="font-semibold text-amber-700 text-xs whitespace-nowrap">+{k.points}pt</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatMini({ icon: Icon, label, value, suffix, color }) {
  const cmap = { amber: "text-amber-700 bg-amber-100", emerald: "text-emerald-700 bg-emerald-100", sky: "text-sky-700 bg-sky-100" };
  return (
    <div className={`rounded-lg p-3 bg-white/50 border border-slate-100 flex items-center gap-2`}>
      <div className={`w-8 h-8 rounded-full ${cmap[color] || cmap.amber} flex items-center justify-center`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-bold text-slate-800 leading-none">{value}{suffix && <span className="text-xs font-medium text-slate-500 ml-0.5">{suffix}</span>}</div>
        <div className="text-[11px] text-slate-500 leading-none mt-1">{label}</div>
      </div>
    </div>
  );
}
